/**
 * Rendu hors ecran d'une retouche composite. Sert aux miniatures de la galerie,
 * au replay et a l'image de partage : un seul chemin de rendu, donc les trois
 * sont forcement coherents.
 *
 * Tout est local : aucun pixel ne quitte le navigateur.
 */
import { decodeDrawing, sliceDrawing, type DrawZone, type Drawing } from '@retusch/shared';
import { Compositor } from '../game/canvas/Compositor.js';
import { ZoneCanvas } from '../game/canvas/ZoneCanvas.js';
import { ZONE_CANVAS_WIDTH } from '../game/canvas/renderConfig.js';
import { fitViewport, zoneToRect, type Size } from '../game/canvas/geometry.js';
import { loadArtwork, naturalSize } from '../game/artwork.js';

const GOLD = '#e8b84b';

export interface ArtworkRenderInput {
  readonly file: string;
  readonly zone: DrawZone;
  readonly palette: readonly string[];
  /** Blob opaque tel que recu du serveur, ou null si l'auteur n'a rien rendu. */
  readonly drawing: ArrayBuffer | null;
  /**
   * Mode collaboratif : les retouches SUCCESSIVES du meme tableau, dans
   * l'ordre ou elles ont ete posees, chacune avec la palette de son auteur.
   *
   * Le vote les rejoue une par une, en gardant les precedentes a l'ecran :
   * c'est ainsi que le tableau se construit sous les yeux de celui qui juge.
   */
  readonly layers?: readonly ArtworkLayer[];
}

export interface ArtworkLayer {
  readonly drawing: ArrayBuffer | null;
  readonly palette: readonly string[];
}

export interface ArtworkRenderOptions {
  readonly size: number;
  /** Rendu non carre : le tableau remplit la boite en debordant (cover). */
  readonly viewport?: { readonly width: number; readonly height: number };
  /** Decalage vertical du cadrage, en fraction de la hauteur (cover seul). */
  readonly focusY?: number;
  /** Liseré or autour de la zone retouchee. */
  readonly badge?: boolean;
  /** Fraction des traits a rendre (replay). 1 = dessin complet. */
  readonly progress?: number;
}

function decode(drawing: ArrayBuffer | null): Drawing {
  if (drawing === null || drawing.byteLength === 0) return [];
  try {
    return decodeDrawing(drawing);
  } catch {
    // Blob illisible : on rend le tableau avec sa zone blanche plutot que rien.
    return [];
  }
}

export interface PreparedArtwork {
  readonly image: HTMLImageElement;
  readonly imageSize: Size;
  readonly compositor: Compositor;
  readonly zoneCanvas: ZoneCanvas;
  readonly drawing: Drawing;
  /** Rejoue le dessin jusqu'a `progress` (0-1). */
  setProgress(progress: number): void;
  /**
   * Mode collaboratif : les couches 0 a `index-1` completes, la `index`-ieme
   * rejouee jusqu'a `progress`.
   *
   * Les precedentes restent a l'ecran, et c'est tout l'interet : on voit ce que
   * chacun a AJOUTE, pas une suite de dessins independants.
   */
  setLayerProgress(index: number, progress: number): void;
  /** Peint le composite dans un canvas de `size` px de cote. */
  paint(canvas: HTMLCanvasElement, options: ArtworkRenderOptions): void;
}

/**
 * Charge l'image et prepare les canvases. Couteux : a faire une fois par
 * tableau, puis reutiliser pour chaque frame de replay.
 */
export async function prepareArtwork(input: ArtworkRenderInput): Promise<PreparedArtwork> {
  const image = await loadArtwork(input.file);
  const imageSize = naturalSize(image);
  const rect = zoneToRect(input.zone, imageSize);

  const compositor = new Compositor(image, imageSize, input.zone, ZONE_CANVAS_WIDTH);
  const zoneCanvas = new ZoneCanvas(rect.height / rect.width, input.palette);
  const drawing = decode(input.drawing);
  const layers = (input.layers ?? []).map((layer) => ({
    strokes: decode(layer.drawing),
    palette: layer.palette,
  }));

  zoneCanvas.replay(drawing);
  compositor.invalidateZone();

  /** Couches 0..index-1 completes, la `index`-ieme rejouee jusqu'a `progress`. */
  const layerProgress = (index: number, progress: number): void => {
    zoneCanvas.setBase(
      layers.slice(0, Math.max(index, 0)).map((layer) => ({
        strokes: layer.strokes,
        palette: layer.palette,
      })),
    );
    const current = layers[index];
    zoneCanvas.replay(current === undefined ? [] : sliceDrawing(current.strokes, progress));
    compositor.invalidateZone();
  };

  return {
    image,
    imageSize,
    compositor,
    zoneCanvas,
    drawing,

    setProgress(progress: number): void {
      // Tableau collaboratif : pas de dessin propre, des COUCHES. Le rejeu
      // s'etale alors sur toutes, dans l'ordre ou elles ont ete posees : c'est
      // l'œuvre entiere qu'on rejoue, pas la derniere contribution.
      if (drawing.length === 0 && layers.length > 0) {
        const scaled = Math.min(Math.max(progress, 0), 1) * layers.length;
        const index = Math.min(Math.floor(scaled), layers.length - 1);
        layerProgress(index, scaled - index);
        return;
      }
      zoneCanvas.replay(sliceDrawing(drawing, progress));
      compositor.invalidateZone();
    },

    setLayerProgress: layerProgress,

    paint(canvas: HTMLCanvasElement, options: ArtworkRenderOptions): void {
      const box = options.viewport ?? { width: options.size, height: options.size };
      if (canvas.width !== box.width || canvas.height !== box.height) {
        canvas.width = box.width;
        canvas.height = box.height;
      }

      const ctx = canvas.getContext('2d');
      if (ctx === null) return;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, box.width, box.height);

      // `cover` quand une boite non carree est fournie : le fond d'accueil doit
      // remplir l'ecran, pas s'y inscrire avec des bandes.
      const viewport =
        options.viewport === undefined
          ? fitViewport(imageSize, box)
          : (() => {
              const scale = Math.max(box.width / imageSize.width, box.height / imageSize.height);
              return {
                scale,
                tx: (box.width - imageSize.width * scale) / 2,
                ty:
                  (box.height - imageSize.height * scale) / 2 +
                  (options.focusY ?? 0) * box.height,
              };
            })();
      compositor.draw(ctx, viewport, zoneCanvas.canvas, { grain: true, feather: true });

      if (options.badge !== true) return;

      ctx.save();
      ctx.strokeStyle = GOLD;
      ctx.lineWidth = Math.max(1, box.width / 220);
      ctx.shadowColor = GOLD;
      ctx.shadowBlur = box.width / 40;
      ctx.strokeRect(
        input.zone.x * imageSize.width * viewport.scale + viewport.tx,
        input.zone.y * imageSize.height * viewport.scale + viewport.ty,
        input.zone.width * imageSize.width * viewport.scale,
        input.zone.height * imageSize.height * viewport.scale,
      );
      ctx.restore();
    },
  };
}

/**
 * Couleurs prelevees AUTOUR d'une zone, dans l'œuvre elle-meme.
 *
 * Sert au fond d'accueil : une retouche de demonstration peinte avec une palette
 * generique ressort comme un gribouillis pose sur le tableau. Avec les teintes du
 * voisinage immediat, elle se lit comme ce qu'elle est : une piece qui cherche a
 * se fondre.
 *
 * Le prelevement se fait sur un anneau autour de la zone, jamais dedans : dans
 * une vraie partie ce qui est sous le masque est precisement ce qu'on ignore.
 */
export function sampleZonePalette(
  image: CanvasImageSource,
  imageSize: Size,
  zone: DrawZone,
  count: number,
): string[] {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(imageSize.width));
  canvas.height = Math.max(1, Math.round(imageSize.height));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return [];
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const centerX = (zone.x + zone.width / 2) * canvas.width;
  const centerY = (zone.y + zone.height / 2) * canvas.height;
  const radiusX = (zone.width / 2) * canvas.width;
  const radiusY = (zone.height / 2) * canvas.height;

  const colors: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = (i / count) * Math.PI * 2;
    // Rayons alternes : la palette attrape le proche et le lointain plutot que
    // `count` nuances d'un meme aplat.
    const spread = 1.25 + (i % 3) * 0.55;
    const x = Math.round(centerX + Math.cos(angle) * radiusX * spread);
    const y = Math.round(centerY + Math.sin(angle) * radiusY * spread);
    const px = Math.min(Math.max(x, 0), canvas.width - 1);
    const py = Math.min(Math.max(y, 0), canvas.height - 1);
    const [r, g, b] = ctx.getImageData(px, py, 1, 1).data;
    const hex = (value: number | undefined): string => (value ?? 0).toString(16).padStart(2, '0');
    colors.push(`#${hex(r)}${hex(g)}${hex(b)}`);
  }
  return colors;
}

/** Miniature ponctuelle, rendue puis serialisee en dataURL. */
export async function renderThumbnail(
  input: ArtworkRenderInput,
  size: number,
): Promise<string> {
  const prepared = await prepareArtwork(input);
  const canvas = document.createElement('canvas');
  prepared.paint(canvas, { size, badge: true });
  return canvas.toDataURL('image/png');
}
